import type { MatchRequestPayload } from "../types/matching";
import type { Product } from "../types/product";
import type { QuestionDef } from "../types/questions";
import type { SubmissionPayload } from "../types/submission";
import type { LoadedQuestions, WidgetDataSource } from "./types";

function assertApiBase(apiBase: string): void {
  if (!apiBase || apiBase.trim() === "") {
    throw new Error("LiveDataSource requires a non-empty apiBase.");
  }
}

function parseWidgetQuestionsPayload(raw: unknown): LoadedQuestions {
  if (!raw || typeof raw !== "object") {
    throw new Error("Unexpected widget-questions payload shape");
  }

  const payload = raw as Record<string, unknown>;
  const version = typeof payload.version === "string" ? payload.version : "";
  const rootQuestionId =
    typeof payload.rootQuestionId === "string" ? payload.rootQuestionId : "";
  const questionGraphRaw = payload.questionGraph;

  if (!version || !rootQuestionId) {
    throw new Error("Unexpected widget-questions payload: missing version or rootQuestionId");
  }

  if (!questionGraphRaw || typeof questionGraphRaw !== "object") {
    throw new Error("Unexpected widget-questions payload: missing questionGraph");
  }

  const questionGraph: Record<string, QuestionDef> = {};
  for (const [key, value] of Object.entries(questionGraphRaw as Record<string, unknown>)) {
    if (!value || typeof value !== "object") {
      continue;
    }
    questionGraph[key] = value as QuestionDef;
  }

  return { version, rootQuestionId, questionGraph };
}

export class LiveDataSource implements WidgetDataSource {
  private readonly apiBase: string;

  constructor(apiBase: string) {
    assertApiBase(apiBase);
    this.apiBase = apiBase.replace(/\/$/, "");
  }

  async loadQuestions(): Promise<LoadedQuestions> {
    const response = await fetch(`${this.apiBase}/widget-questions`);
    if (!response.ok) {
      throw new Error(`Failed to load widget questions: ${response.status}`);
    }

    const data: unknown = await response.json();
    return parseWidgetQuestionsPayload(data);
  }

  async matchProducts(payload: MatchRequestPayload): Promise<Product[]> {
    const response = await fetch(`${this.apiBase}/widget-match`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to match products: ${response.status}`);
    }

    const data = (await response.json()) as { products?: Product[] };
    if (!data.products || !Array.isArray(data.products)) {
      throw new Error("Unexpected widget-match payload shape");
    }

    return data.products;
  }

  async submitLead(payload: SubmissionPayload): Promise<void> {
    const response = await fetch(`${this.apiBase}/widget-lead`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to submit widget lead: ${response.status}`);
    }
    const result: unknown = await response.json();
    if (!result || typeof result !== "object" || (result as { success?: unknown }).success !== true) {
      throw new Error("Widget lead submission was not confirmed");
    }
  }
}
